// Client-side PDF text extraction using pdf.js
// This allows processing large PDFs without hitting server memory limits

import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Set up the worker using the bundled worker file
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Type for pdf.js outline items (not exported from pdfjs-dist types)
interface PDFOutlineItem {
  title: string;
  dest: unknown;
  items?: PDFOutlineItem[];
}

export interface PDFExtractionResult {
  text: string;
  pageCount: number;
  pages: { pageNumber: number; text: string }[];
}

export interface PDFExtractionProgress {
  currentPage: number;
  totalPages: number;
  percent: number;
  stage?: 'loading' | 'extracting' | 'complete';
}

export interface PDFChunk {
  pageStart: number;
  pageEnd: number;
  text: string;
}

export interface PDFOutlineEntry {
  id: string;
  level: number;
  title: string;
  pageNumber: number;
  children?: PDFOutlineEntry[];
}

export interface PDFOutline {
  entries: PDFOutlineEntry[];
}

/**
 * Extract text from a PDF file client-side
 * @param file - The PDF file to process
 * @param onProgress - Optional callback for progress updates
 * @returns Extracted text and page information
 */
export async function extractTextFromPDF(
  file: File,
  onProgress?: (progress: PDFExtractionProgress) => void
): Promise<PDFExtractionResult> {
  console.log('[pdf.ts] Starting PDF extraction:', { name: file.name, size: file.size, type: file.type });

  // Convert file to ArrayBuffer
  console.log('[pdf.ts] Reading file as ArrayBuffer...');
  const arrayBuffer = await file.arrayBuffer();
  console.log('[pdf.ts] ArrayBuffer loaded, size:', arrayBuffer.byteLength);

  // Load the PDF document
  console.log('[pdf.ts] Loading PDF document with pdf.js...');
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  console.log('[pdf.ts] PDF loaded, pages:', pdf.numPages);
  const totalPages = pdf.numPages;

  const pages: { pageNumber: number; text: string }[] = [];
  const textParts: string[] = [];

  // Extract text from each page
  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    // Combine text items into page text
    const pageText = textContent.items
      .map((item) => {
        if ('str' in item) {
          return item.str;
        }
        return '';
      })
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (pageText) {
      pages.push({ pageNumber: pageNum, text: pageText });
      textParts.push(`[Page ${pageNum}]\n${pageText}`);
    }

    // Report progress
    if (onProgress) {
      onProgress({
        currentPage: pageNum,
        totalPages,
        percent: Math.round((pageNum / totalPages) * 100),
      });
    }
  }

  return {
    text: textParts.join('\n\n'),
    pageCount: totalPages,
    pages,
  };
}

/**
 * Check if a file is a PDF
 */
export function isPDF(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

/**
 * Process a large PDF in chunks, calling onChunk for each batch of pages.
 * This allows streaming large PDFs without loading all text into memory.
 *
 * @param file - The PDF file to process
 * @param onChunk - Callback called with each chunk of extracted text
 * @param onProgress - Optional callback for progress updates
 * @param pagesPerChunk - Number of pages to process per chunk (default: 20)
 */
export async function extractPDFInChunks(
  file: File,
  onChunk: (chunk: PDFChunk, chunkIndex: number, totalChunks: number) => Promise<void>,
  onProgress?: (progress: PDFExtractionProgress) => void,
  pagesPerChunk: number = 20
): Promise<{ pageCount: number; chunkCount: number }> {
  console.log('[pdf.ts] Starting chunked PDF extraction:', { name: file.name, size: file.size, pagesPerChunk });

  // Report loading stage
  onProgress?.({ currentPage: 0, totalPages: 0, percent: 0, stage: 'loading' });

  // Convert file to ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();
  console.log('[pdf.ts] ArrayBuffer loaded, size:', arrayBuffer.byteLength);

  // Load the PDF document
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const totalPages = pdf.numPages;
  const totalChunks = Math.ceil(totalPages / pagesPerChunk);

  console.log('[pdf.ts] PDF loaded, pages:', totalPages, 'chunks:', totalChunks);

  let chunkIndex = 0;

  // Process pages in chunks
  for (let startPage = 1; startPage <= totalPages; startPage += pagesPerChunk) {
    const endPage = Math.min(startPage + pagesPerChunk - 1, totalPages);
    const chunkTextParts: string[] = [];

    // Extract text from each page in this chunk
    for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      const pageText = textContent.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (pageText) {
        chunkTextParts.push(`[Page ${pageNum}]\n${pageText}`);
      }

      // Report progress
      onProgress?.({
        currentPage: pageNum,
        totalPages,
        percent: Math.round((pageNum / totalPages) * 100),
        stage: 'extracting',
      });
    }

    // Send this chunk to the callback
    const chunkText = chunkTextParts.join('\n\n');
    if (chunkText.trim()) {
      await onChunk(
        { pageStart: startPage, pageEnd: endPage, text: chunkText },
        chunkIndex,
        totalChunks
      );
    }

    chunkIndex++;
  }

  onProgress?.({ currentPage: totalPages, totalPages, percent: 100, stage: 'complete' });

  return { pageCount: totalPages, chunkCount: totalChunks };
}

/**
 * Extract the embedded outline (table of contents/bookmarks) from a PDF.
 * This is more reliable than parsing text since it uses the PDF's built-in structure.
 *
 * @param file - The PDF file to process
 * @returns The PDF outline if available, null otherwise
 */
export async function extractPDFOutline(file: File): Promise<PDFOutline | null> {
  console.log('[pdf.ts] Extracting PDF outline:', file.name);

  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    // Get the embedded outline (bookmarks/ToC)
    const outline = await pdf.getOutline();

    if (!outline || outline.length === 0) {
      console.log('[pdf.ts] No embedded outline found in PDF');
      return null;
    }

    console.log('[pdf.ts] Found embedded outline with', outline.length, 'top-level entries');

    // Helper to generate unique IDs
    let idCounter = 0;
    const generateId = () => `outline-${++idCounter}`;

    // Helper to get page number from destination
    const getPageNumber = async (dest: unknown): Promise<number> => {
      try {
        if (!dest) return 1;

        // Destination can be a name or an array
        let destArray: unknown = dest;
        if (typeof dest === 'string') {
          const resolved = await pdf.getDestination(dest);
          if (!resolved) return 1;
          destArray = resolved;
        }

        if (Array.isArray(destArray) && destArray.length > 0) {
          const ref = destArray[0];
          if (ref && typeof ref === 'object' && 'num' in ref) {
            const pageIndex = await pdf.getPageIndex(ref);
            return pageIndex + 1; // Convert 0-indexed to 1-indexed
          }
        }
      } catch (e) {
        console.warn('[pdf.ts] Could not resolve page number for destination:', e);
      }
      return 1;
    };

    // Recursively process outline items
    const processOutlineItems = async (
      items: PDFOutlineItem[],
      level: number = 0
    ): Promise<PDFOutlineEntry[]> => {
      const entries: PDFOutlineEntry[] = [];

      for (const item of items) {
        const pageNumber = await getPageNumber(item.dest);

        const entry: PDFOutlineEntry = {
          id: generateId(),
          level,
          title: item.title || 'Untitled',
          pageNumber,
        };

        // Process children recursively
        if (item.items && item.items.length > 0) {
          entry.children = await processOutlineItems(item.items, level + 1);
        }

        entries.push(entry);
      }

      return entries;
    };

    const entries = await processOutlineItems(outline);

    console.log('[pdf.ts] Processed outline:', entries.length, 'entries');

    return { entries };
  } catch (error) {
    console.error('[pdf.ts] Error extracting PDF outline:', error);
    return null;
  }
}

/**
 * Extract text and outline from a PDF file.
 * Combines text extraction with outline extraction for complete document info.
 */
export async function extractPDFWithOutline(
  file: File,
  onProgress?: (progress: PDFExtractionProgress) => void
): Promise<PDFExtractionResult & { outline: PDFOutline | null }> {
  // Extract text and outline in parallel
  const [textResult, outline] = await Promise.all([
    extractTextFromPDF(file, onProgress),
    extractPDFOutline(file),
  ]);

  return {
    ...textResult,
    outline,
  };
}
